'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'convex/react'
import { api } from '@/convex/_generated/api'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { CheckCircle, FileText, Pen, Type, Loader2 } from 'lucide-react'
import type { Id } from '@/convex/_generated/dataModel'

// ─── Constants ───────────────────────────────────────────────
const CURRENT_CONSENT_VERSION = 'v1.0'

// ─── Types ───────────────────────────────────────────────────
interface ConsentGateProps {
  phone: string
  phoneVerified: boolean
  patientName: string
  patientConvexId?: string | null
  patientHumanId?: string | null
  onConsentStatusChange: (signed: boolean) => void
}

// ─── Consent Form Content (exact wording from PDF) ───────────
function ConsentFormContent() {
  return (
    <div className="space-y-3 text-xs sm:text-sm text-gray-700 leading-relaxed">
      <div className="text-center mb-4">
        <h3 className="text-sm sm:text-base font-bold text-gray-900">
          ReACH PSYCHIATRY &amp; COUNSELLING CENTRE
        </h3>
        <p className="text-[10px] sm:text-xs text-gray-600">
          Dr Meena Gnanasekharan, MD, DABPN
        </p>
        <p className="text-xs sm:text-sm font-semibold mt-2 text-gray-800">
          CONSENT FORM FOR IN-PERSON PSYCHOLOGICAL THERAPY
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">1. Purpose</p>
        <p>
          This is an informed consent agreement for in-person/online
          psychological therapy services. This agreement aims to inform you
          about the nature of the therapeutic process, the limits of
          confidentiality, the risks and benefits of therapy, and to obtain your
          consent to participate in therapy.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">2. Nature of Therapy</p>
        <p>
          Psychological therapy at ReACH Psychiatry involves meeting with a
          licensed Psychiatrist and a designated Psychologist to discuss and
          address your mental health concerns and well-being. The focus of
          therapy will depend on your needs and goals and involves discussing
          thoughts, feelings, behaviours, and relationships. Sessions will
          comprise details of your chief complaints, past psychiatric and
          medical history, assessments, and treatment plans will be discussed
          throughout regular therapy sessions.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">
          3. Confidentiality and Limits to Confidentiality
        </p>
        <p>
          ReACH Psychiatry guarantees providing psychotherapy and counseling
          service while maintaining confidentiality and security of any personal
          information collected from patients. Patients have a fundamental right
          to the confidentiality of their information, with specific limitations
          outlined below:
        </p>
        <p className="mt-2">
          All health professionals providing care or treatment to a person with
          mental illness shall have a duty to keep all such information
          confidential which has been obtained during care or treatment with the
          following exceptions:
        </p>

        <div className="mt-2 space-y-2 pl-3 sm:pl-4">
          <p>
            <span className="font-medium">3.1</span> When the patient offers
            written consent and waives their right to privacy for specific
            purposes such as accommodations for school/university/work, or any
            third party such as family members.
          </p>
          <p>
            <span className="font-medium">3.2</span> When the patient is unable
            to contract for safety and disclosure it is necessary, based on
            competent clinical judgment, to prevent obvious and immediate harm
            to the patient/client or others.
          </p>
          <p>
            <span className="font-medium">3.3</span> The release of information
            upon an order by concerned Board or the Central Authority or High
            Court or Supreme Court or any other statutory authority competent to
            do so, in cases involving the abuse or welfare of minors,
            individuals in need of special protection, or the elderly.
          </p>
          <p>
            <span className="font-medium">3.4</span> If your prior written
            approval has been obtained to share specific information with a
            third party.
          </p>
          <p>
            <span className="font-medium">3.5</span> Release of information to
            other mental health professionals and other health professionals to
            enable them to provide care and treatment to the person with mental
            illness.
          </p>
          <p>
            <span className="font-medium">3.6</span> (d) Only such information
            that is necessary to protect against the harm identified shall be
            released; (e) Release only such information as is necessary to
            prevent threat to life; (g) Release of information in the interests
            of public safety and security.
          </p>
          <p>
            <span className="font-medium">3.7</span> Your case information may
            be discussed within the clinical team for the purposes of
            professional supervision and ensuring quality care and a
            collaborative approach to treatment. All such discussions will
            remain strictly confidential and will not be shared outside the team
            or made public.
          </p>
        </div>
      </div>

      <div>
        <p className="font-semibold text-gray-800">
          4. Medical Information and Records
        </p>
        <p>
          No personal or any identifiable information from therapy sessions will
          be shared with any other party without the patient/client&apos;s
          written consent. The therapy sessions adhere to all applicable federal
          laws and local regulations, policies, and guidelines related to
          accessing medical information and obtaining copies of the
          patient/client&apos;s health records.
        </p>

        <div className="mt-2">
          <p className="font-medium">4.1 Access to Files:</p>
          <p className="mt-1 font-medium">
            I consent to The ReACH Psychiatry using aspects of this and sharing
            the same (limited to the extent necessary) for legitimate purposes,
            and to meet statutory obligations, including:
          </p>
          <p className="mt-1">Sharing health data with:</p>
          <p className="mt-1">
            An explicit request is required for clients to access their records.
            The session notes maintained as per the format may be shared under
            the conditions specified in the Mental Health Care Act, 2017.
          </p>
          <div className="mt-1 space-y-1 pl-3 sm:pl-4">
            <p>
              <span className="font-medium">a.</span> Other healthcare providers
              within the clinic for the purpose of enhancing your overall care.
              It&apos;s important to note that your personal data is treated
              with utmost confidentiality.
            </p>
            <p>
              <span className="font-medium">b.</span> Any relevant government
              authority in accordance with statutory obligations such as release
              of information upon an order by concerned Board or the Central
              Authority or High Court or Supreme Court or any other statutory
              authority competent to do so.
            </p>
          </div>
        </div>
      </div>

      <div>
        <p className="font-semibold text-gray-800">5. Expected Benefits</p>
        <p>
          Engaging in therapy allows you to gain insight into your thoughts,
          behaviours, and emotions, and learn effective strategies for managing
          them. It can also help you develop a healthier relationship with
          yourself and increase your self-awareness and well-being.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">6. Emergency Contact</p>
        <p>
          If there is a crisis involving your mental health, ReACH Psychiatry
          will get in touch with this person. This need not necessarily be the
          spouse, adult children, siblings, or parents; it can be any of them.
          Please make sure that the contact information you have provided is
          current and that you are a reliable person who can be reached quickly
          in case of emergency.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">7. Working with Minors</p>
        <p>
          In the case of minors (those under the age of 18) parents have certain
          restricted access rights to records, to foster trust and safeguard the
          therapeutic relationship between the minor and the psychologist. The
          psychologist will notify parents if they believe there is a
          significant chance the minor will self-harm, hurt others, or engage in
          destructive behaviours.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">8. Termination Policy</p>
        <p>The termination policy is applicable in three instances:</p>
        <div className="mt-1 space-y-2 pl-3 sm:pl-4">
          <p>
            &bull; The therapy ends when the goals that have been set at the
            beginning of the therapy sessions have been met.
          </p>
          <p>
            &bull; If the patient/client requests to end the therapy at any
            moment. Please discuss any worries you may have with your
            psychologist so that adjustments can be made to help you benefit
            more from therapy. It&apos;s common for a patient/client to consult
            with multiple psychologists before determining who is the &quot;best
            fit.&quot; If you would like assistance finding a psychologist who
            could be a better fit for you, please discuss your preferences with
            the psychologist.
          </p>
          <p>
            &bull; The Psychologist can terminate therapy when it becomes
            reasonably clear that the patient/client no longer needs the service
            or is not likely to benefit from continued service. The psychologist
            will discuss the reasons for termination with the patient/client,
            ensuring that the patient/client understands the rationale behind
            the decision and provide them with advance notice of the
            termination, whenever possible, to allow for a smooth transition.
            The psychologist will also provide guidance on finding alternative
            sources of support, if necessary, and offer referrals to other
            mental health professionals or resources.
          </p>
          <p>
            &bull;{' '}
            <span className="font-medium">Termination Due to Conduct:</span> The
            practice reserves the right to terminate services if a client
            engages in behaviour that is threatening, abusive, persistently
            belligerent, or otherwise disruptive to the therapeutic process or
            the safety and wellbeing of staff or others. In such circumstances,
            termination will be handled professionally and ethically, and
            appropriate referrals may be provided where feasible.
          </p>
        </div>
      </div>

      <div>
        <p className="font-semibold text-gray-800">9. Possible Risks</p>
        <p>
          Although therapy can be beneficial, it may also involve discussing
          painful or difficult experiences, which can be emotionally
          challenging. However, this will occur within a safe, secure place that
          is non-judgmental and supportive. This environment is designed to
          ensure your confidentiality and provide you with the necessary support
          throughout the therapeutic process.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">10. Cancellation Policy</p>
        <p>
          Due to the high volume of patients/clients seeking our services, we
          require that you attend all scheduled sessions unless we are notified
          24 hours in advance. If not you may be charged a nominal fee for the
          same.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">11. Late arrivals</p>
        <p>
          Our clinic aims to provide a pleasurable experience for our all
          patients/clients when visiting our clinic by minimizing your wait
          time. In light of this, we have implemented the late arrival policy to
          ensure patients/clients that arrive on time are seen in a timely
          manner. If a patient/client arrives late, the appointment will be
          shortened to the remaining treatment time and the appointment will be
          charged in full.
        </p>
      </div>

      <div>
        <p className="font-semibold text-gray-800">12. Working with Minors</p>
        <p>
          In the case of minors (those under the age of 18) parents have certain
          restricted access rights to records, to foster trust and safeguard the
          therapeutic relationship between the minor and the psychologist. The
          psychologist will notify parents if they believe there is a
          significant chance the minor will self-harm, hurt others, or engage in
          destructive behaviors.
        </p>
      </div>

      <div className="mt-4 pt-3 border-t border-gray-200">
        <p className="font-semibold text-gray-800">
          By signing below, you acknowledge that you have read and understood
          the information and agree to participate in psychological therapy with
          ReACH Psychiatry &amp; Counselling Centre.
        </p>
      </div>
    </div>
  )
}

// ─── Signature Pad (canvas-based) ────────────────────────────
function SignaturePad({
  onSignatureChange,
}: {
  onSignatureChange: (data: string | null) => void
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const isDrawingRef = useRef(false)
  const hasDrawnRef = useRef(false)

  const getPos = (
    e: React.MouseEvent | React.TouchEvent,
    canvas: HTMLCanvasElement,
  ) => {
    const rect = canvas.getBoundingClientRect()
    if ('touches' in e) {
      const touch = e.touches[0]
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top }
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const startDraw = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current
    if (!canvas) return
    isDrawingRef.current = true
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const pos = getPos(e, canvas)
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const pos = getPos(e, canvas)
    ctx.lineTo(pos.x, pos.y)
    ctx.strokeStyle = '#1a1a1a'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()
    hasDrawnRef.current = true
  }

  const endDraw = () => {
    isDrawingRef.current = false
    if (hasDrawnRef.current && canvasRef.current) {
      onSignatureChange(canvasRef.current.toDataURL('image/png'))
    }
  }

  const clear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    hasDrawnRef.current = false
    onSignatureChange(null)
  }

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const dpr = window.devicePixelRatio || 1
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * dpr
    canvas.height = rect.height * dpr
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.scale(dpr, dpr)
  }, [])

  return (
    <div className="flex flex-col gap-1.5">
      <div className="relative rounded-md border-2 border-dashed border-gray-300 bg-white">
        <canvas
          ref={canvasRef}
          className="w-full h-28 sm:h-36 cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={(e) => {
            e.preventDefault()
            startDraw(e)
          }}
          onTouchMove={(e) => {
            e.preventDefault()
            draw(e)
          }}
          onTouchEnd={endDraw}
        />
        <p className="absolute bottom-1 left-2 text-[9px] sm:text-[10px] text-gray-400 pointer-events-none">
          Sign above using mouse or finger
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={clear}
        className="self-end text-[10px] sm:text-xs h-6 sm:h-7 px-2"
      >
        Clear signature
      </Button>
    </div>
  )
}

// ─── Main ConsentGate Component ──────────────────────────────
const ConsentGate = ({
  phone,
  phoneVerified,
  patientName,
  patientConvexId,
  patientHumanId,
  onConsentStatusChange,
}: ConsentGateProps) => {
  const [sigMode, setSigMode] = useState<'drawn' | 'typed'>('drawn')
  const [drawnSignature, setDrawnSignature] = useState<string | null>(null)
  const [typedName, setTypedName] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [signing, setSigning] = useState(false)
  const [justSigned, setJustSigned] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)

  // Track which patient the justSigned flag belongs to
  const [signedForPatientId, setSignedForPatientId] = useState<string | null>(
    null,
  )

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const signConsentMutation = useMutation(api.patientConsent.sign)

  // Normalize phone for lookup
  const normalizedPhone = useMemo(
    () => (phone || '').replace(/\D/g, '').slice(-10),
    [phone],
  )

  const lookupEnabled = phoneVerified && normalizedPhone.length === 10

  // Stable key for the current patient selection
  const currentPatientKey = patientConvexId ?? '__new__'

  // Reset local signing state when patient selection changes
  useEffect(() => {
    if (
      signedForPatientId !== null &&
      signedForPatientId !== currentPatientKey
    ) {
      setJustSigned(false)
      setSignedForPatientId(null)
      setAgreed(false)
      setDrawnSignature(null)
      setTypedName('')
      setSignError(null)
      setHasScrolledToBottom(false)
    }
  }, [currentPatientKey, signedForPatientId])

  // Query existing consent — pass patientConvexId for precise lookup
  const existingConsent = useQuery(
    api.patientConsent.getByPhone,
    lookupEnabled
      ? {
          phone: normalizedPhone,
          consentVersion: CURRENT_CONSENT_VERSION,
          patientConvexId: patientConvexId
            ? (patientConvexId as Id<'patients'>)
            : undefined,
        }
      : 'skip',
  )

  // Derive consent status
  const consentAlreadySigned = !!existingConsent
  const consentSigned =
    consentAlreadySigned ||
    (justSigned && signedForPatientId === currentPatientKey)

  // Notify parent of consent status changes
  useEffect(() => {
    onConsentStatusChange(consentSigned)
  }, [consentSigned, onConsentStatusChange])

  // Track scroll to bottom of consent form
  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30
    if (atBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true)
    }
  }, [hasScrolledToBottom])

  // Check if signature is provided
  const hasSignature =
    sigMode === 'drawn' ? !!drawnSignature : typedName.trim().length >= 2

  const canSubmit = agreed && hasSignature && hasScrolledToBottom

  const handleSign = async () => {
    if (!canSubmit) return
    setSigning(true)
    setSignError(null)

    try {
      const signatureData =
        sigMode === 'drawn' ? drawnSignature! : typedName.trim()

      await signConsentMutation({
        phone: normalizedPhone,
        patient_convex_id: patientConvexId
          ? (patientConvexId as Id<'patients'>)
          : undefined,
        patient_id: patientHumanId ?? undefined,
        patient_name: patientName || typedName.trim(),
        consent_version: CURRENT_CONSENT_VERSION,
        signature_data: signatureData,
        signature_type: sigMode,
        signed_via: 'booking_flow',
      })

      setJustSigned(true)
      setSignedForPatientId(currentPatientKey)
    } catch (err: any) {
      const msg =
        typeof err?.message === 'string'
          ? err.message
          : 'Failed to record consent. Please try again.'
      setSignError(msg)
    } finally {
      setSigning(false)
    }
  }

  // Don't render anything until we know the consent status
  if (!lookupEnabled) return null
  if (existingConsent === undefined && !justSigned) {
    return (
      <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground py-2">
        <span className="h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        Checking consent status…
      </div>
    )
  }

  // Already signed — show compact confirmation
  if (consentSigned) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2.5 sm:px-4 sm:py-3">
        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
        <div>
          <p className="text-xs sm:text-sm font-medium text-green-800">
            Consent form signed
          </p>
          <p className="text-[10px] sm:text-xs text-green-600">
            {justSigned && signedForPatientId === currentPatientKey
              ? 'Thank you for signing the consent form.'
              : `Previously signed (${CURRENT_CONSENT_VERSION})`}
          </p>
        </div>
      </div>
    )
  }

  // Not signed — show the full consent gate
  return (
    <div className="flex flex-col gap-3 sm:gap-4 rounded-xl border-2 border-amber-200 bg-amber-50/50 p-3 sm:p-4">
      {/* Header */}
      <div className="flex flex-col items-center text-center">
        <div className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
          <FileText className="h-4 w-4 text-amber-700 sm:h-5 sm:w-5" />
        </div>

        <h4 className="text-base font-semibold text-amber-900 sm:text-lg">
          Consent Form
        </h4>

        <p className="mt-1 max-w-xl text-center text-xs leading-5 text-amber-700 sm:text-sm">
          Please read and sign the consent form below to proceed with your
          booking.
        </p>
      </div>

      {/* Scrollable consent form */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="max-h-52 sm:max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 sm:p-4 shadow-inner"
      >
        <ConsentFormContent />
      </div>

      {!hasScrolledToBottom && (
        <p className="text-[10px] sm:text-xs text-amber-700 text-center animate-pulse">
          ↓ Please scroll to the bottom to read the full consent form
        </p>
      )}

      {/* Signature section - only fully interactive after scrolling */}
      <div
        className={`flex flex-col gap-3 transition-opacity duration-300 ${
          hasScrolledToBottom ? 'opacity-100' : 'opacity-40 pointer-events-none'
        }`}
      >
        {/* Mode toggle */}
        <div className="flex items-center gap-2">
          <Label className="text-xs sm:text-sm text-gray-700">
            Sign using:
          </Label>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              type="button"
              onClick={() => setSigMode('drawn')}
              className={`flex items-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-xs font-medium transition-colors ${
                sigMode === 'drawn'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Pen className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              Draw
            </button>
            <button
              type="button"
              onClick={() => setSigMode('typed')}
              className={`flex items-center gap-1 px-2.5 py-1.5 sm:px-3 sm:py-2 text-[10px] sm:text-xs font-medium transition-colors border-l ${
                sigMode === 'typed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Type className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              Type Name
            </button>
          </div>
        </div>

        {/* Signature input */}
        {sigMode === 'drawn' ? (
          <SignaturePad onSignatureChange={setDrawnSignature} />
        ) : (
          <div className="flex flex-col gap-1.5">
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your full name as signature"
              className="text-sm sm:text-base h-9 sm:h-10 font-medium italic"
            />
            {typedName.trim().length > 0 && typedName.trim().length < 2 && (
              <p className="text-[10px] sm:text-xs text-red-600">
                Please enter at least 2 characters.
              </p>
            )}
          </div>
        )}

        {/* Agreement checkbox */}
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-[10px] sm:text-xs text-gray-700 leading-relaxed">
            I have read and understood the information above. I agree to
            participate in psychological therapy at ReACH Psychiatry &amp;
            Counselling Centre as described in this consent form.
          </span>
        </label>

        {/* Error message */}
        {signError && (
          <p className="text-[10px] sm:text-xs text-red-600 font-medium">
            {signError}
          </p>
        )}

        {/* Submit */}
        <Button
          type="button"
          disabled={!canSubmit || signing}
          onClick={handleSign}
          className="w-full bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm h-9 sm:h-10 disabled:opacity-50"
        >
          {signing ? (
            <>
              <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 animate-spin" />
              Signing…
            </>
          ) : (
            <>
              <Pen className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5" />
              Sign Consent Form
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

export default ConsentGate
